import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore, useThemeStore } from '../stores';
import { API_URL } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  User,
  Building2,
  Moon,
  Sun,
  LogOut,
  Package,
  Users,
  FileText,
  Calendar,
  ChevronRight,
  Shield,
  Loader2,
  Eye,
  EyeOff,
  Tags,
  Plus,
  Trash2,
} from 'lucide-react';

const CategoriesTab = () => {
  const { token } = useAuthStore();
  const queryClient = useQueryClient();
  const [newCat, setNewCat] = useState('');

  const { data: categories = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/categories`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Failed');
      return res.json();
    },
  });

  const addMutation = useMutation({
    mutationFn: async (name) => {
      const res = await fetch(`${API_URL}/categories`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name }),
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Erreur'); }
      return res.json();
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['categories'] }); toast.success('Catégorie ajoutée'); setNewCat(''); },
    onError: (err) => toast.error(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      const res = await fetch(`${API_URL}/categories/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Erreur');
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['categories'] }); toast.success('Catégorie supprimée'); },
    onError: () => toast.error('Erreur'),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg font-heading flex items-center gap-2">
          <Tags className="w-5 h-5" /> Catégories de produits
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2">
          <Input
            value={newCat}
            onChange={(e) => setNewCat(e.target.value)}
            placeholder="Nouvelle catégorie..."
            onKeyDown={(e) => { if (e.key === 'Enter' && newCat.trim()) { e.preventDefault(); addMutation.mutate(newCat.trim()); } }}
            data-testid="new-category-input"
          />
          <Button onClick={() => { if (newCat.trim()) addMutation.mutate(newCat.trim()); }} disabled={!newCat.trim()} data-testid="add-category-btn">
            <Plus className="w-4 h-4" />
          </Button>
        </div>
        <div className="space-y-1">
          {categories.map((cat) => (
            <div key={cat.id} className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-secondary/50">
              <div className="flex items-center gap-2">
                <Badge variant="outline">{cat.slug}</Badge>
                <span className="text-sm font-medium">{cat.name}</span>
              </div>
              <Button
                variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive"
                onClick={() => { if (window.confirm(`Supprimer la catégorie "${cat.name}" ?`)) deleteMutation.mutate(cat.id); }}
                data-testid={`delete-category-${cat.slug}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}
          {categories.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-4">Aucune catégorie. Ajoutez-en une ci-dessus.</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const SecurityTab = () => {
  const { token } = useAuthStore();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showNew, setShowNew] = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/users/me/password`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Erreur');
      toast.success('Mot de passe modifié');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg font-heading flex items-center gap-2">
          <Shield className="w-5 h-5" /> Changer le mot de passe
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
          <div className="space-y-2">
            <Label>Mot de passe actuel</Label>
            <Input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              placeholder="Votre mot de passe actuel"
              data-testid="current-password"
            />
          </div>
          <div className="space-y-2">
            <Label>Nouveau mot de passe</Label>
            <div className="relative">
              <Input
                type={showNew ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={6}
                placeholder="Minimum 6 caractères"
                className="pr-10"
                data-testid="new-password"
              />
              <Button type="button" variant="ghost" size="icon" className="absolute right-0 top-0 h-full px-3" onClick={() => setShowNew(!showNew)}>
                {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </Button>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Confirmer</Label>
            <Input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              placeholder="Retapez le nouveau mot de passe"
              data-testid="confirm-password"
            />
          </div>
          <Button type="submit" disabled={loading} data-testid="change-password-btn">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Modifier le mot de passe
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

const SettingsPage = () => {
  const { user, logout } = useAuthStore();
  const { theme, toggleTheme } = useThemeStore();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const menuItems = [
    { to: '/products', icon: Package, label: 'Produits', description: 'Gérer le catalogue' },
    { to: '/clients', icon: Users, label: 'Clients', description: 'Base clients' },
    { to: '/quotes', icon: FileText, label: 'Devis', description: 'Propositions commerciales' },
    { to: '/events', icon: Calendar, label: 'Événements', description: 'Planning' },
  ];

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-heading font-bold">Paramètres</h1>
        <p className="text-muted-foreground">Gérez votre compte et préférences</p>
      </div>

      {/* User Profile Card */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              <span className="text-2xl font-bold text-primary">
                {user?.full_name?.charAt(0)?.toUpperCase() || 'U'}
              </span>
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-heading font-semibold">{user?.full_name}</h2>
              <p className="text-muted-foreground">{user?.email}</p>
              {user?.company_name && (
                <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                  <Building2 className="w-4 h-4" /> {user.company_name}
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs: Apparence + Sécurité */}
      <Tabs defaultValue="appearance">
        <TabsList>
          <TabsTrigger value="appearance" data-testid="tab-appearance">Apparence</TabsTrigger>
          <TabsTrigger value="categories" data-testid="tab-categories">Matériel</TabsTrigger>
          <TabsTrigger value="security" data-testid="tab-security">Sécurité</TabsTrigger>
        </TabsList>

        <TabsContent value="appearance" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-heading">Apparence</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {theme === 'dark' ? <Moon className="w-5 h-5 text-primary" /> : <Sun className="w-5 h-5 text-primary" />}
                  <div>
                    <Label className="text-base">Mode sombre</Label>
                    <p className="text-sm text-muted-foreground">
                      {theme === 'dark' ? 'Activé - Idéal pour le backstage' : 'Désactivé - Mode bureau'}
                    </p>
                  </div>
                </div>
                <Switch checked={theme === 'dark'} onCheckedChange={toggleTheme} data-testid="dark-mode-switch" />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="categories" className="space-y-4 mt-4">
          <CategoriesTab />
        </TabsContent>

        <TabsContent value="security" className="space-y-4 mt-4">
          <SecurityTab />
        </TabsContent>
      </Tabs>

      {/* Quick Navigation (Mobile) */}
      <Card className="md:hidden">
        <CardHeader>
          <CardTitle className="text-lg font-heading">Navigation rapide</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {menuItems.map((item, index) => (
            <React.Fragment key={item.to}>
              <Link
                to={item.to}
                className="flex items-center justify-between p-4 hover:bg-secondary transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <item.icon className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium">{item.label}</p>
                    <p className="text-sm text-muted-foreground">{item.description}</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground" />
              </Link>
              {index < menuItems.length - 1 && <Separator />}
            </React.Fragment>
          ))}
        </CardContent>
      </Card>

      {/* Logout */}
      <Card className="border-destructive/20">
        <CardContent className="p-4">
          <Button variant="destructive" className="w-full" onClick={handleLogout} data-testid="logout-settings-btn">
            <LogOut className="w-4 h-4 mr-2" /> Déconnexion
          </Button>
        </CardContent>
      </Card>

      <p className="text-center text-sm text-muted-foreground">EventPro ERP v1.0.0</p>
    </div>
  );
};

export default SettingsPage;
